<template>
    <web :title="page?.seo_title ?? page?.title" :meta_description="page?.meta_description" :page="page"
        :fixed-breadcrumb="true">
        <template #head>
            <component v-if="headComponent" :is="headComponent" />
        </template>

        <template #header v-if="page">
            <component v-if="introComponent" :is="introComponent" />
        </template>

        <div v-if="page">
            <!-- Posts mount the related-tours rail beside the article (vertical
                 sticky sidebar on desktop, carousel on mobile). The two-column
                 grid only engages once tours have resolved, so a post with no
                 matches — and every plain CMS page — renders full width. -->
            <div v-if="withRelatedTours"
                :class="showRail ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_25rem]' : ''">
                <div class="min-w-0">
                    <component v-if="contentComponent" :is="contentComponent" />
                </div>
                <!-- Rail column. The border-l lives here (not on the sticky
                     aside) so the accent line runs the full height of the
                     article, like the quote/FAQ cards. The cell stretches to
                     the taller column because the grid no longer pins items to
                     the start. -->
                <div class="lg:border-l lg:border-neutral-200">
                    <Deferred :data="['related_tours']">
                        <template #fallback><span /></template>
                        <related-tours :tours="related_tours" :heading="page.related_tours_heading"
                            :content-block-id="railContentBlockId" />
                    </Deferred>
                </div>
            </div>
            <component v-else-if="contentComponent" :is="contentComponent" />
        </div>

        <channel-status v-if="channel" :loading="loading" :connected="connected" :connecting="connecting"
            :reconnect="reconnect" />

        <page-status :status="page.status" />
    </web>
</template>

<script setup lang="ts">
import Web from "@/Layouts/Web.vue";
import defineContent from "@/utils/render";
import { Page as TPage, Tour } from "@/types";
import { computed, onMounted, ref, shallowRef } from "vue";
import { Deferred } from "@inertiajs/vue3";
import ChannelStatus from "@/Components/ChannelStatus.vue";
import PageStatus from "@/Components/PageStatus.vue";
import RelatedTours from "@/Components/Tour/RelatedTours.vue";
import useDataLayer from "@/Hook/useDataLayer";
import { useLiveRender } from "@/Hook/useLiveRender";

const props = defineProps<{
    page: TPage;
    channel?: string;
    // Set only when rendered as an inspiration post (see PostsController).
    withRelatedTours?: boolean;
    // Deferred — undefined until the rail's lazy request resolves.
    related_tours?: Tour[];
}>();

const hasRelatedTours = computed(() => !!props.related_tours?.length);

// Admin-selected content block for the rail — available synchronously on the
// page payload (unlike the deferred related tours).
const railContentBlockId = computed(() => props.page?.rail_content_block_id ?? null);

// Engage the two-column rail layout when there's anything to show in the rail:
// related tours (once they resolve) or a content block.
const showRail = computed(() => hasRelatedTours.value || !!railContentBlockId.value);

type ContentComponent = ReturnType<typeof defineContent> | null;

const headComponent = shallowRef<ContentComponent>(null);
const introComponent = shallowRef<ContentComponent>(null);
const contentComponent = shallowRef<ContentComponent>(null);

let lastHeadHtml: string | null = null;
let lastIntroHtml: string | null = null;
let lastContentHtml: string | null = null;

const page = ref(props.page);

const { registerPageVisit } = useDataLayer()


const liveRender = useLiveRender<any>(function ({ data }) {
    page.value = { ...page.value, ...data };
    setContent();
}, props.channel);

const { loading, connected, connecting, connect: reconnect } = liveRender



// Build the renderable content components from the page HTML. Pure and
// SSR-safe (defineContent only parses at render time, via the isomorphic
// parser), so this can run during setup to populate the server-rendered markup.
const syncContent = function () {
    const html = page.value.content?.html ?? {};
    const nextIntro = html.intro ?? null;
    const nextContent = html.content ?? html.body ?? null;
    const nextHead = html.head ?? null;

    if (nextIntro !== lastIntroHtml) {
        lastIntroHtml = nextIntro;
        introComponent.value = nextIntro ? defineContent(nextIntro) : null;
    }
    if (nextContent !== lastContentHtml) {
        lastContentHtml = nextContent;
        contentComponent.value = nextContent ? defineContent(nextContent) : null;
    }
    if (nextHead !== lastHeadHtml) {
        lastHeadHtml = nextHead;
        headComponent.value = nextHead ? defineContent(nextHead) : null;
    }
};

// Inject the author's <style>/<script> into the document. Browser-only — there
// is no document during SSR, and these assets drive client styling/behaviour,
// not the server-rendered markup.
const injectAssets = function () {
    if (typeof document === "undefined") return;

    const styleId = "page-style-element";
    const scriptId = "page-script-element";

    let styleElement = document.getElementById(styleId);
    let scriptElement = document.getElementById(scriptId);

    if (styleElement) {
        styleElement.innerHTML = page.value.content.css ?? "";
    } else {
        styleElement = document.createElement("style");
        styleElement.id = styleId;
        styleElement.setAttribute("type", "text/css");
        styleElement.innerHTML = page.value.content.css ?? "";
        document.head.appendChild(styleElement);
    }

    if (scriptElement) {
        scriptElement.innerHTML = page.value.content?.js?.script ?? "";
    } else {
        scriptElement = document.createElement("script");
        scriptElement.id = scriptId;
        scriptElement.innerHTML = page.value.content.js?.script ?? "";
        document.body.appendChild(scriptElement);
    }
};

const setContent = function () {
    try {
        syncContent();
        injectAssets();
    } catch (err) {
        console.error(err);
    }
};

// Build content synchronously during setup so it is part of the server-rendered
// HTML (SEO + first paint); onMounted re-runs to inject author assets in-browser.
syncContent();

onMounted(function () {
    setContent();
    if (!props.channel) registerPageVisit(props.page)
});
</script>
